import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaClock, 
  FaMapMarkerAlt, 
  FaUserPlus, 
  FaUsers, 
  FaCarSide,
  FaStar,
  FaWalking,
  FaCar,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import PostDetailsModal from '../PostDetailsModal';
import ChatV2 from '../Chat/ChatV2';
import EventMapView from './components/EventMapView';
import Spinner from '../common/Spinner';
import CustomButton9, { CustomButton1, CustomButton10, CustomButton10Ca, CustomButton2, CustomButton3, CustomButton4, CustomButton5, CustomButton6, CustomButton7, CustomButton8 } from '../Templates';
import { useToast } from '../hooks/ToastContext';
import './styles/EventDetailsPage.css';

function EventDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user, loading: userLoading } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // State
  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [rides, setRides] = useState([]);
  const [rideRequests, setRideRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userAttendanceStatus, setUserAttendanceStatus] = useState(null);
  
  // Post details modal state
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [modalPostId, setModalPostId] = useState(null);
  const [modalPostType, setModalPostType] = useState('post');
  
  // Pagination state
  const [ridePage, setRidePage] = useState(1);
  const [requestPage, setRequestPage] = useState(1);
  const ridesPerPage = 3;
  
  // Check user authentication
  useEffect(() => {
    const checkUserAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error || !data?.session) {
          console.log('No authenticated user found, redirecting to login');
          navigate('/login');
          return;
        }
      } catch (err) {
        console.error('Session check error:', err);
        navigate('/login');
      }
    };
    
    checkUserAuth();
  }, [navigate]);
  
  // Fetch event data - Only run this effect when user is available and not loading
  useEffect(() => {
    // Early return if no ID or user is still loading
    if (!id || userLoading) return;
    
    const fetchEventData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch event details
        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select('*')
          .eq('id', id)
          .single();
          
        if (eventError) throw eventError;
        
        if (!eventData) {
          throw new Error(t('errors.event_not_found'));
        }
        
        // Fetch creator info separately
        const { data: creatorData, error: creatorError } = await supabase
          .from('profiles')
          .select('id, username, avatar_url')
          .eq('id', eventData.creator_id)
          .single();
          
        if (creatorError && creatorError.code !== 'PGRST116') {
          console.error('Error fetching creator:', creatorError);
        }
        
        // Attach creator to event
        setEvent({
          ...eventData,
          creator: creatorData || { id: eventData.creator_id }
        });
        
        // Fetch attendees
        const { data: attendeesRaw, error: attendeesError } = await supabase
          .from('event_attendees')
          .select('*')
          .eq('event_id', id)
          .order('created_at', { ascending: true });
          
        if (attendeesError) throw attendeesError;
        
        // Fetch attendee profiles
        let attendeesWithProfiles = [];
        
        if (attendeesRaw && attendeesRaw.length > 0) {
          // Get unique user IDs
          const userIds = [...new Set(attendeesRaw.map(a => a.user_id))];
          
          // Fetch profiles
          const { data: userProfiles } = await supabase
            .from('profiles')
            .select('id, username, avatar_url')
            .in('id', userIds);
            
          // Join attendees with profiles
          attendeesWithProfiles = attendeesRaw.map(attendee => ({
            ...attendee,
            profile: { 
              profiles: [userProfiles?.find(p => p.id === attendee.user_id) || { id: attendee.user_id }]
            }
          }));
        }
        
        setAttendees(attendeesWithProfiles);
        
        // Check if current user is attending - only if user exists
        if (user) {
          const userAttendance = attendeesRaw?.find(a => a.user_id === user.id);
          setUserAttendanceStatus(userAttendance ? userAttendance.status : null);
        }
        
        // Fetch rides to this event
        const { data: ridesRaw, error: ridesError } = await supabase
          .from('event_rides')
          .select('*')
          .eq('event_id', id)
          .order('created_at', { ascending: false });
          
        if (ridesError) throw ridesError;
        
        // Fetch driver profiles
        let ridesWithDrivers = [];
        
        if (ridesRaw && ridesRaw.length > 0) {
          // Get unique driver IDs
          const driverIds = [...new Set(ridesRaw.map(r => r.driver_id))];
          
          // Fetch profiles
          const { data: driverProfiles } = await supabase
            .from('profiles')
            .select('id, username, avatar_url')
            .in('id', driverIds);
            
          // Join rides with profiles
          ridesWithDrivers = ridesRaw.map(ride => ({
            ...ride,
            driver_profile: { 
              profiles: [driverProfiles?.find(p => p.id === ride.driver_id) || { id: ride.driver_id }]
            }
          }));
        }
        
        setRides(ridesWithDrivers);
        
        // Fetch ride requests for this event - NEW
        const { data: requestsRaw, error: requestsError } = await supabase
          .from('event_ride_requests')
          .select('*')
          .eq('event_id', id)
          .in('status', ['active', 'pending'])  // Include both active and pending requests
          .order('created_at', { ascending: false });
        
        console.log('Fetched ride requests:', requestsRaw);
        
        if (requestsError) throw requestsError;
        
        // Fetch passenger profiles - NEW
        let requestsWithProfiles = [];
        
        if (requestsRaw && requestsRaw.length > 0) {
          // Get unique passenger IDs
          const passengerIds = [...new Set(requestsRaw.map(r => r.passenger_id))];
          
          // Fetch profiles
          const { data: passengerProfiles } = await supabase
            .from('profiles')
            .select('id, username, avatar_url')
            .in('id', passengerIds);
            
          // Join requests with profiles
          requestsWithProfiles = requestsRaw.map(request => ({
            ...request,
            passenger_profile: { 
              profiles: [passengerProfiles?.find(p => p.id === request.passenger_id) || { id: request.passenger_id }]
            }
          }));
        }
        
        setRideRequests(requestsWithProfiles);
      } catch (err) {
        console.error('Error fetching event details:', err);
        setError(err.message);
        
        addToast({
          type: 'error',
          title: t('errors.fetch_error'),
          description: err.message
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEventData();
  }, [id, user, userLoading, t, addToast]);
  
  // Format date 
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Format time in 24-hour format
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(undefined, { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false // Use 24-hour format
    });
  };

  // Handle attendance status change
  const handleAttendanceChange = async (status) => {
    // Safety check for user existence
    if (!user || !user.id) {
      addToast({
        type: 'error',
        title: t('errors.authentication_required'),
        description: t('errors.login_required_action')
      });
      navigate('/login');
      return;
    }
    
    try {
      if (userAttendanceStatus) {
        // Update existing attendance record
        const { error } = await supabase
          .from('event_attendees')
          .update({ status })
          .eq('event_id', id)
          .eq('user_id', user.id);
          
        if (error) throw error;
      } else {
        // Create new attendance record
        const { error } = await supabase
          .from('event_attendees')
          .insert({
            event_id: id,
            user_id: user.id,
            status
          });
          
        if (error) throw error;
      }
      
      // Update local state
      setUserAttendanceStatus(status);
      
      // Update attendees list
      const updatedAttendees = [...attendees];
      const existingIndex = updatedAttendees.findIndex(a => a.user_id === user.id);
      
      if (existingIndex >= 0) {
        updatedAttendees[existingIndex].status = status;
      } else {
        // Add new attendee to the list
        updatedAttendees.push({
          event_id: parseInt(id),
          user_id: user.id,
          status,
          created_at: new Date().toISOString(),
          user: { id: user.id, email: user.email },
          profile: { profiles: [{ id: user.id }] }
        });
      }
      
      setAttendees(updatedAttendees);
      
      addToast({
        type: 'success',
        title: t('events.attendance_updated'),
        description: status === 'going' ? 
          t('events.successfully_joined') : 
          t('events.marked_as_interested')
      });
    } catch (err) {
      console.error('Error updating attendance status:', err);
      
      addToast({
        type: 'error',
        title: t('errors.update_failed'),
        description: t('errors.attendance_update_failed')
      });
    }
  };

  // Handle going to event
  const handleGoingClick = () => {
    handleAttendanceChange('going');
  };

  // Handle interested in event
  const handleInterestedClick = () => {
    handleAttendanceChange('interested');
  };

  // Handle cancelling attendance
  const handleCancelAttendance = async () => {
    // Safety check for user existence
    if (!user || !user.id) {
      addToast({
        type: 'error',
        title: t('errors.authentication_required'),
        description: t('errors.login_required_action')
      });
      navigate('/login');
      return;
    }
    
    try {
      const { error } = await supabase
        .from('event_attendees')
        .delete()
        .eq('event_id', id)
        .eq('user_id', user.id);
        
      if (error) throw error;
      
      // Update local state
      setUserAttendanceStatus(null);
      
      // Update attendees list
      const updatedAttendees = attendees.filter(a => a.user_id !== user.id);
      setAttendees(updatedAttendees);
      
      addToast({
        type: 'info',
        title: t('events.attendance_cancelled'),
        description: t('events.successfully_cancelled')
      });
    } catch (err) {
      console.error('Error cancelling attendance:', err);
      
      addToast({
        type: 'error',
        title: t('errors.update_failed'),
        description: t('errors.attendance_cancel_failed')
      });
    }
  };

  // Handle offer ride button
  const handleOfferRide = () => {
    navigate(`/events/${id}/ride?mode=offer`);
  };

  // Handle looking for ride button
  const handleLookingForRide = () => {
    navigate(`/events/${id}/ride?mode=request`);
  };

  // Handle back button
  const handleBackClick = () => {
    navigate('/events');
  };

  // Handle view ride details
  const handleViewRide = (rideId) => {
    console.log('Opening modal for ride with ID:', rideId);
    if (!rideId) {
      console.error('Ride ID is undefined or null');
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: 'Invalid ride ID'
      });
      return;
    }
    
    // Open the post details in a modal instead of navigating
    setModalPostId(rideId);
    setModalPostType('post');
    setIsPostModalOpen(true);
  };

  // Handle view ride request details
  const handleViewRequest = (requestId) => {
    console.log('Opening modal for request with ID:', requestId);
    if (!requestId) {
      console.error('Request ID is undefined or null');
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: 'Invalid request ID'
      });
      return;
    }
    
    // If it's a ride request, get the associated post ID if available
    const getPostIdForRequest = async (reqId) => {
      try {
        // First, check if the request has a matched_ride_id or notes with Post ID
        const { data, error } = await supabase
          .from('event_ride_requests')
          .select('notes, matched_ride_id')
          .eq('id', reqId)
          .single();
        
        console.log('Ride request data:', data);  
        if (error) throw error;
        
        // First check if there's a matched_ride_id
        if (data && data.matched_ride_id) {
          console.log('Found matched_ride_id:', data.matched_ride_id);
          return data.matched_ride_id.toString();
        }
        
        // Then check for Post ID in notes
        if (data && data.notes) {
          // Try to extract post ID from notes
          const postIdMatch = data.notes.match(/Post ID: (\d+)/);
          if (postIdMatch && postIdMatch[1]) {
            console.log('Found Post ID in notes:', postIdMatch[1]);
            return postIdMatch[1];
          }
        }
        
        return null;
      } catch (err) {
        console.error('Error getting post ID for request:', err);
        return null;
      }
    };
    
    // Attempt to get a post ID from the request
    getPostIdForRequest(requestId).then(postId => {
      if (postId) {
        // If we found a post ID, open the post details modal
        console.log('Found associated post ID:', postId, 'for request:', requestId);
        setModalPostId(postId);
        setModalPostType('post');
        setIsPostModalOpen(true);
      } else {
        // Otherwise fall back to the request view in modal
        setModalPostId(requestId);
        setModalPostType('request');
        setIsPostModalOpen(true);
      }
    });
  };

  // Handle message user - NEW
  const handleMessageUser = (userId) => {
    // Need to implement the messaging feature
    // This would typically create or open a conversation with this user
    
    // For now, we'll use a placeholder that navigates to the chat and shows a toast
    addToast({
      type: 'info',
      title: t('messages.new_message'),
      description: t('messages.select_user_in_chat')
    });
    
    // You might want to update this to directly open a chat with the specific user
    // For example, navigating to a specific chat URL or opening a chat modal
  };

  // Show loading spinner while fetching data or user is loading
  if (isLoading || userLoading) {
    return (
      <div className="event-details-loading">
        <Spinner 
          size="large" 
          customText={t('app.loading')}
          fullPage
        />
      </div>
    );
  }

  // Ensure user is defined and redirect if not (after loading is complete)
  if (!user && !userLoading) {
    navigate('/login');
    return null;
  }

  // Show error message if there was a problem
  if (error) {
    return (
      <>
        <div className="event-details-error">
          <h2>{t('errors.error_title')}</h2>
          <p>{error}</p>
          <CustomButton2 
            label={t('buttons.go_back')} 
            onClick={handleBackClick}
          />
        </div>
      </>
    );
  }

  // Ensure event is defined before rendering the rest
  if (!event) {
    return (
      <div className="event-details-loading">
        <Spinner 
          size="large" 
          customText={t('app.loading_event')}
          fullPage
        />
      </div>
    );
  }

  // Default banner image if none provided
  const defaultBanner = 'https://placehold.co/1200x400/2B3D4F/9FC131';
  const bannerImageUrl = event?.banner_image_url || defaultBanner;

  // Get attendees count by status
  const goingCount = attendees.filter(a => a.status === 'going').length;
  const interestedCount = attendees.filter(a => a.status === 'interested').length;

  // Determine whether to show the map view
  // Only show if there are rides or ride requests, or if the user is attending
  const shouldShowMapView = rides.length > 0 || rideRequests.length > 0 || userAttendanceStatus === 'going';

  // Helper function for pagination
  const getPaginatedItems = (items, page, itemsPerPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    return items.slice(startIndex, startIndex + itemsPerPage);
  };

  // Calculate pagination parameters
  const totalRidePages = Math.ceil(rides.length / ridesPerPage);
  const totalRequestPages = Math.ceil(rideRequests.length / ridesPerPage);
  const paginatedRides = getPaginatedItems(rides, ridePage, ridesPerPage);
  const paginatedRequests = getPaginatedItems(rideRequests, requestPage, ridesPerPage);

  // Handle pagination
  const handleRidePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalRidePages) {
      setRidePage(newPage);
    }
  };

  const handleRequestPageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalRequestPages) {
      setRequestPage(newPage);
    }
  };

  return (
    <>
      <div className="event-details-page">
        <div className="back-button-container">
          <CustomButton8 onClick={handleBackClick}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        
        <div className="event-banner" style={{ backgroundImage: `url(${bannerImageUrl})` }}>
          <div className="event-banner-overlay">
            <h1 className="event-title">{event.title}</h1>
          </div>
        </div>
        
        <div className="event-content">
          <div className="event-main-details">
            <div className="event-info-container">
              <div className="event-info">
                <div className="event-detail">
                  <FaCalendarAlt />
                  <span>{formatDate(event.start_date)}</span>
                </div>
                
                <div className="event-detail">
                  <FaClock />
                  <span>{formatTime(event.start_date)}</span>
                </div>
                
                <div className="event-detail">
                  <FaMapMarkerAlt />
                  <span>{event.location}</span>
                </div>
                
                <div className="event-detail">
                  <FaUsers />
                  <span>
                    {goingCount} {t('events.going')}, {interestedCount} {t('events.interested')}
                  </span>
                </div>
              </div>
              
              <div className="event-actions">
                {!userAttendanceStatus ? (
                  // Not attending yet, show the join button
                  <CustomButton2 
                    label={t('events.join_event')}
                    onClick={handleGoingClick}
                    className="going-button"
                    icon={<FaUserPlus />}
                  />
                ) : userAttendanceStatus === 'going' ? (
                  // Already attending, show the ride options
                  <div className="ride-action-buttons">
                    <CustomButton2 
                      label={t('events.offer_ride')}
                      onClick={handleOfferRide}
                      className="offer-ride-button"
                      icon={<FaCarSide />}
                    />
                    <CustomButton2 
                      label={t('events.looking_for_ride')}
                      onClick={handleLookingForRide}
                      className="need-ride-button"
                      icon={<FaWalking />}
                    />
                    <CustomButton2
                      label={t('events.cancel_attendance')}
                      onClick={handleCancelAttendance}
                      className="cancel-button"
                    />
                  </div>
                ) : (
                  // Interested but not going, show options to join
                  <div className="attendance-buttons">
                    <CustomButton2 
                      label={t('events.join_event')}
                      onClick={handleGoingClick}
                      className="going-button"
                      icon={<FaUserPlus />}
                    />
                    <CustomButton2
                      label={t('events.cancel_attendance')}
                      onClick={handleCancelAttendance}
                      className="cancel-button"
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* MAP VIEW - NEW SECTION */}
            {shouldShowMapView && (
              <EventMapView 
                event={event}
                rides={rides}
                rideRequests={rideRequests}
                onViewRide={handleViewRide}
                onViewRequest={handleViewRequest}
                onMessageUser={handleMessageUser}
              />
            )}
            
            <div className="event-description">
              <h3>{t('events.description')}</h3>
              {event.description ? (
                <p>{event.description}</p>
              ) : (
                <div className="event-description-empty">
                  {t('events.no_description_available')}
                </div>
              )}
            </div>
          </div>
          
          <div className="event-sidebar">
            <div className="event-section">
              <h3><FaCarSide /> {t('events.ride_offers')}</h3>
              {rides.length > 0 ? (
                <>
                  <div className="rides-list">
                    {paginatedRides.map(ride => (
                      <div className="ride-card" key={ride.id}>
                        <div className="ride-info">
                          <h4>{ride.driver_profile?.profiles[0]?.username || 'Driver'}</h4>
                          <div className="ride-detail">
                            <FaMapMarkerAlt />
                            <span>{ride.departure_location}</span>
                          </div>
                          <div className="ride-detail">
                            <FaClock />
                            <span>{formatTime(ride.departure_time)}</span>
                          </div>
                          <div className="ride-detail">
                            <FaUsers />
                            <span>
                              {ride.seats_available}/{ride.seats_offered} {t('posts.seats_available')}
                            </span>
                          </div>
                        </div>
                        <CustomButton2 
                          label={t('events.view_ride')}
                          onClick={() => handleViewRide(ride.post_id)}
                          className="view-ride-button"
                        />
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination controls for ride offers */}
                  {totalRidePages > 1 && (
                    <div className="pagination-controls">
                      <button 
                        className="pagination-button" 
                        onClick={() => handleRidePageChange(ridePage - 1)}
                        disabled={ridePage === 1}
                      >
                        <FaChevronLeft /> {t('pagination.prev')}
                      </button>
                      
                      <div className="pagination-info">
                        {ridePage} / {totalRidePages}
                      </div>
                      
                      <button 
                        className="pagination-button" 
                        onClick={() => handleRidePageChange(ridePage + 1)}
                        disabled={ridePage === totalRidePages}
                      >
                        {t('pagination.next')} <FaChevronRight />
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-rides">
                  <p>{t('events.no_rides_yet')}</p>
                  {userAttendanceStatus === 'going' && (
                    <CustomButton2 
                      label={t('events.offer_ride')}
                      onClick={handleOfferRide}
                      className="offer-ride-button"
                      icon={<FaCarSide />}
                    />
                  )}
                </div>
              )}
            </div>
            
            {/* NEW SECTION: Ride Requests */}
            {rideRequests.length > 0 && (
              <div className="event-section">
                <h3><FaWalking /> {t('events.ride_requests')}</h3>
                <div className="rides-list ride-requests-list">
                  {paginatedRequests.map(request => (
                    <div className="ride-card request-card" key={request.id}>
                      <div className="ride-info">
                        <h4>{request.passenger_profile?.profiles[0]?.username || 'Passenger'}</h4>
                        <div className="ride-detail">
                          <FaMapMarkerAlt />
                          <span>{request.pickup_location}</span>
                        </div>
                        {request.notes && (
                          <div className="ride-detail notes">
                            <span>{request.notes}</span>
                          </div>
                        )}
                      </div>
                        <CustomButton2 
                        label={t('events.view_request')}
                        onClick={() => handleViewRequest(request.id)}
                        className="view-request-button"
                      />
                    </div>
                  ))}
                </div>

                {/* Pagination controls for ride requests */}
                {totalRequestPages > 1 && (
                  <div className="pagination-controls">
                    <button 
                      className="pagination-button" 
                      onClick={() => handleRequestPageChange(requestPage - 1)}
                      disabled={requestPage === 1}
                    >
                      <FaChevronLeft /> {t('pagination.prev')}
                    </button>
                    
                    <div className="pagination-info">
                      {requestPage} / {totalRequestPages}
                    </div>
                    
                    <button 
                      className="pagination-button" 
                      onClick={() => handleRequestPageChange(requestPage + 1)}
                      disabled={requestPage === totalRequestPages}
                    >
                      {t('pagination.next')} <FaChevronRight />
                    </button>
                  </div>
                )}
              </div>
            )}
            
            <div className="event-section">
              <h3><FaUsers /> {t('events.attendees')}</h3>
              <div className="attendees-list">
                {attendees.filter(a => a.status === 'going').map(attendee => (
                  <div className="attendee" key={attendee.id}>
                    <div className="attendee-avatar">
                      {attendee.profile?.profiles[0]?.username?.[0] || 'U'}
                    </div>
                    <div className="attendee-name">
                      {attendee.profile?.profiles[0]?.username || 'User'}
                    </div>
                    <div className="attendee-badges">
                      {attendee.user_id === event.creator_id && (
                        <div className="organizer-badge">
                          <FaStar />
                          <span>{t('events.organizer')}</span>
                        </div>
                      )}
                      {attendee.role === 'driver' && (
                        <div className="driver-badge">
                          <FaCar />
                          <span>{t('events.driver')}</span>
                        </div>
                      )}
                      {attendee.role === 'passenger' && (
                        <div className="passenger-badge">
                          <FaWalking />
                          <span>{t('events.passenger')}</span>
                        </div>
                      )}
                      {!attendee.role && attendee.status === 'going' && (
                        <div className="attendee-badge">
                          <FaUsers />
                          <span>{t('events.attendee')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {goingCount === 0 && (
                  <p className="no-attendees">{t('events.no_attendees_yet')}</p>
                )}
              </div>
              
              {interestedCount > 0 && (
                <div className="interested-section">
                  <h4><FaStar /> {t('events.interested_people')}</h4>
                  <div className="attendees-list">
                    {attendees.filter(a => a.status === 'interested').map(attendee => (
                      <div className="attendee" key={attendee.id}>
                        <div className="attendee-avatar interested">
                          {attendee.profile?.profiles[0]?.username?.[0] || 'U'}
                        </div>
                        <div className="attendee-name">
                          {attendee.profile?.profiles[0]?.username || 'User'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ChatV2 />

      {/* Post Details Modal */}
      <PostDetailsModal
        isOpen={isPostModalOpen}
        onClose={() => setIsPostModalOpen(false)}
        id={modalPostId}
        type={modalPostType}
      />
    </>
  );
}

export default EventDetailsPage;